import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { useStores } from '../../hooks/use-stores';
import VisualEditor from '../../components/manager/assessment/visual-editor/VisualEditor';
import { 
    FlexDiv, 
    Block,
    Heading3,
    ThemedButton,
    TextDefault
} from '@awarego-ui/components';
import { Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function QuestionEditor() {
    const { store, brandingStore } = useStores();
    const { currentCompany } = store;
    const { questionsEditorVisible } = brandingStore;
    const navigate = useNavigate();
    const { questionId } = useParams();
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Determine if we're editing an existing question or creating a new one
    const isEditing = Boolean(questionId);
    const initialQuestion = null; // TODO: Load existing question if editing

    const handleSave = async (question) => {
        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // TODO: Implement actual save logic
            // This would typically call an API to save the question
            console.log('Saving question:', question);
            
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            setSuccess(isEditing ? 'Question updated successfully!' : 'Question created successfully!');
            
            // Redirect back to content page after a short delay
            setTimeout(() => {
                navigate('/content');
            }, 1500);
            
        } catch (err) {
            console.error('Failed to save question:', err);
            setError(err.message || 'Failed to save question. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/content');
    };

    const handleBack = () => {
        navigate('/content');
    };

    // Redirect if questions editor is not visible
    if (!questionsEditorVisible) {
        navigate('/content');
        return null;
    }

    return (
        <Container className="manager-content">
            <Helmet>
                <title>{isEditing ? 'Edit Question' : 'Create Question'}</title>
            </Helmet>
            
            <Block padding={24} gap={24}>
                {/* Header */}
                <FlexDiv alignCenter gap={16}>
                    <ThemedButton
                        mode="secondary"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        text="Back to Content"
                    />
                    <Heading3>
                        {isEditing ? 'Edit Question' : 'Create New Question'}
                    </Heading3>
                </FlexDiv>

                {/* Status Messages */}
                {error && (
                    <Alert color="danger">
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert color="success">
                        {success}
                    </Alert>
                )}

                {/* Instructions */}
                <Block padding={16} style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <TextDefault>
                        <strong>Instructions:</strong> Use the visual editor below to create your assessment question. 
                        Drag blocks from the left palette to build interactive content, add presentations, 
                        and configure properties in the right panel. Use the preview button to test your question.
                    </TextDefault>
                </Block>

                {/* Visual Editor */}
                <div style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '8px',
                    overflow: 'hidden',
                    minHeight: '600px'
                }}>
                    <VisualEditor
                        initialQuestion={initialQuestion}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        companyId={currentCompany?.company_id}
                    />
                </div>

                {/* Loading Overlay */}
                {isLoading && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}>
                        <Block padding={32} style={{ 
                            backgroundColor: 'white', 
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <TextDefault>Saving question...</TextDefault>
                        </Block>
                    </div>
                )}
            </Block>
        </Container>
    );
}

export default observer(QuestionEditor);
